import { apiRequest, PlayerApiError } from '@/api/client';
import { pushWarning } from '@/lib/warningsBuffer';

/**
 * The player's executor for remote commands (D1, D7).
 *
 * The SSE event only says "look"; the runner reads `GET /player/commands` and
 * acts on what is there, so a command issued while the stream was down is still
 * picked up when it reconnects — unless it expired first, which the server
 * decides (D8).
 *
 * A reload cannot report its own success: the page that would report it is the
 * one being thrown away. So the runner reports `delivered`, leaves itself a note
 * in sessionStorage, and reloads; the next page finds the note and reports
 * `succeeded`. sessionStorage survives `location.reload()` and not a kiosk
 * restart, which is exactly right — a browser that died on the way down did not
 * reload, and the server fails the command when no confirmation arrives.
 */

export const PENDING_RELOAD_KEY = 'l42.player.pendingReload';

export interface PlayerCommand {
  id: string;
  kind: string;
  issuedAt: string;
  expiresAt: string;
}

type Request = <T>(path: string, opts?: { method?: 'GET' | 'POST'; body?: unknown }) => Promise<T>;

export interface CommandRunnerOptions {
  /** Injectable for tests; defaults to the authenticated player client. */
  request?: Request;
  /** What a reload actually does. PlaybackView passes its covered reload. */
  reload?: () => void | Promise<void>;
  buildVersion?: string;
}

export interface CommandRunner {
  /** Fetch pending commands and run them in order. Safe to call concurrently. */
  drain(): Promise<void>;
  /** Report `succeeded` for a reload this page is the result of. */
  confirmAfterBoot(): Promise<void>;
}

export function createCommandRunner(opts: CommandRunnerOptions = {}): CommandRunner {
  const request: Request = opts.request ?? apiRequest;
  const reload = opts.reload ?? ((): void => window.location.reload());
  const buildVersion = opts.buildVersion ?? 'unknown';

  let inFlight: Promise<void> | null = null;
  let again = false;
  let reloading = false;

  const report = (id: string, body: Record<string, unknown>): Promise<unknown> =>
    request(`/player/commands/${encodeURIComponent(id)}/result`, { method: 'POST', body });

  const run = async (cmd: PlayerCommand): Promise<void> => {
    if (cmd.kind === 'reload') {
      try {
        await report(cmd.id, { status: 'delivered' });
      } catch (err) {
        // A 409 here is the server refusing a command that expired in transit;
        // anything else means we could not tell it we are acting. Either way the
        // reload does not happen — firing late is what expiry exists to stop.
        pushWarning(`command-deliver-failed:${cmd.id}:${errStatus(err)}`);
        return;
      }
      writePendingReload(cmd.id);
      reloading = true;
      await reload();
      return;
    }
    try {
      await report(cmd.id, {
        status: 'failed',
        result: { message: `unsupported by player ${buildVersion}`.slice(0, 500) },
      });
    } catch (err) {
      pushWarning(`command-report-failed:${cmd.id}:${errStatus(err)}`);
    }
  };

  const drainOnce = async (): Promise<void> => {
    await confirmAfterBoot();
    let items: PlayerCommand[];
    try {
      items = (await request<{ items: PlayerCommand[] }>('/player/commands')).items;
    } catch (err) {
      pushWarning(`command-fetch-failed:${errStatus(err)}`);
      return;
    }
    for (const cmd of items) {
      if (reloading) return;
      await run(cmd);
    }
  };

  const drain = (): Promise<void> => {
    if (reloading) return Promise.resolve();
    if (inFlight) {
      // A wake-up during a drain may announce a command the running drain has
      // already listed past. One more pass after it, not one per wake-up.
      again = true;
      return inFlight;
    }
    inFlight = (async () => {
      try {
        do {
          again = false;
          await drainOnce();
        } while (again && !reloading);
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };

  // Called on mount and again by every drain; the mount call and the first
  // drain (the stream opening) usually overlap, and both would read the note.
  let confirming: Promise<void> | null = null;
  const confirmAfterBoot = (): Promise<void> => {
    confirming ??= confirmOnce().finally(() => {
      confirming = null;
    });
    return confirming;
  };

  const confirmOnce = async (): Promise<void> => {
    const id = readPendingReload();
    if (!id) return;
    try {
      await report(id, { status: 'succeeded' });
      clearPendingReload();
    } catch (err) {
      // A 4xx will never succeed (the server already failed or expired it);
      // a network error might, on the next drain.
      if (err instanceof PlayerApiError && err.status >= 400 && err.status < 500) {
        clearPendingReload();
      }
      pushWarning(`command-confirm-failed:${id}:${errStatus(err)}`);
    }
  };

  return { drain, confirmAfterBoot };
}

function errStatus(err: unknown): string {
  return err instanceof PlayerApiError ? String(err.status) : 'network';
}

function readPendingReload(): string | null {
  try {
    return window.sessionStorage.getItem(PENDING_RELOAD_KEY);
  } catch {
    return null;
  }
}

function writePendingReload(id: string): void {
  try {
    window.sessionStorage.setItem(PENDING_RELOAD_KEY, id);
  } catch {
    // Without the note the reload still happens; the server fails the command
    // for want of a confirmation, which is the honest outcome.
  }
}

function clearPendingReload(): void {
  try {
    window.sessionStorage.removeItem(PENDING_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}
